/**
 * ComponentMatrixSection.tsx
 * Component Matrix section showing every HeroUI component with every color option
 */

import React from 'react';
import { Card, CardHeader, CardBody } from '@enfomentis/ui';
import { Button } from '@enfomentis/ui';
import { Badge } from '@enfomentis/ui';
import { Chip } from '@enfomentis/ui';
import { Divider } from '@enfomentis/ui';
import { Link } from '@enfomentis/ui';

// HeroUI semantic colors for component testing
const heroUIColors = ['primary', 'secondary', 'success', 'warning', 'danger', 'default'] as const;

// Link colors (doesn't include 'default', uses 'foreground' instead)
const linkColors = ['primary', 'secondary', 'success', 'warning', 'danger', 'foreground'] as const;

export const ComponentMatrixSection: React.FC = () => {
  return (
    <div className="space-y-6">
      <Card shadow="lg" isHoverable>
        <CardHeader>
          <div className="space-y-2">
            <h3 className="text-xl font-semibold text-text-primary">Component × Color Matrix</h3>
            <Divider />
            <p className="text-text-secondary">HeroUI components with every color option - updates with theme toggle</p>
          </div>
        </CardHeader>
        <CardBody>
          <div className="space-y-8">
            {/* Buttons Matrix */}
            <div>
              <h4 className="text-lg font-semibold text-text-primary mb-4">Buttons</h4>
              <div className="grid gap-4">
                {heroUIColors.map((color) => (
                  <div key={`button-${color}`} className="space-y-3">
                    <h5 className="font-medium text-text-secondary capitalize">{color}</h5>
                    <div className="flex flex-wrap gap-2">
                      <Button color={color} size="sm">Solid</Button>
                      <Button color={color} variant="bordered" size="sm">Bordered</Button>
                      <Button color={color} variant="flat" size="sm">Flat</Button>
                      <Button color={color} variant="faded" size="sm">Faded</Button>
                      <Button color={color} variant="shadow" size="sm">Shadow</Button>
                      <Button color={color} variant="ghost" size="sm">Ghost</Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <Divider />

            {/* Chips Matrix */}
            <div>
              <h4 className="text-lg font-semibold text-text-primary mb-4">Chips</h4>
              <div className="grid gap-4">
                {heroUIColors.map((color) => (
                  <div key={`chip-${color}`} className="space-y-3">
                    <h5 className="font-medium text-text-secondary capitalize">{color}</h5>
                    <div className="flex flex-wrap gap-2">
                      <Chip color={color} size="sm">Solid</Chip>
                      <Chip color={color} variant="bordered" size="sm">Bordered</Chip>
                      <Chip color={color} variant="flat" size="sm">Flat</Chip>
                      <Chip color={color} variant="faded" size="sm">Faded</Chip>
                      <Chip color={color} variant="shadow" size="sm">Shadow</Chip>
                      <Chip color={color} variant="dot" size="sm">Dot</Chip>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <Divider />

            {/* Badges Matrix */}
            <div>
              <h4 className="text-lg font-semibold text-text-primary mb-4">Badges</h4>
              <div className="grid gap-4">
                {heroUIColors.map((color) => (
                  <div key={`badge-${color}`} className="space-y-3">
                    <h5 className="font-medium text-text-secondary capitalize">{color}</h5>
                    <div className="flex flex-wrap gap-3">
                      <Badge color={color} content="3">
                        <Button variant="bordered" size="sm">Solid</Button>
                      </Badge>
                      <Badge color={color} variant="flat" content="99">
                        <Button variant="bordered" size="sm">Flat</Button>
                      </Badge>
                      <Badge color={color} variant="faded" content="1">
                        <Button variant="bordered" size="sm">Faded</Button>
                      </Badge>
                      <Badge color={color} variant="shadow" content="!">
                        <Button variant="bordered" size="sm">Shadow</Button>
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <Divider />

            {/* Links Matrix */}
            <div>
              <h4 className="text-lg font-semibold text-text-primary mb-4">Links</h4>
              <div className="grid gap-4">
                {linkColors.map((color) => (
                  <div key={`link-${color}`} className="space-y-3">
                    <h5 className="font-medium text-text-secondary capitalize">{color === 'foreground' ? 'default' : color}</h5>
                    <div className="flex flex-wrap gap-4">
                      <Link color={color} size="sm">Default Link</Link>
                      <Link color={color} underline="always" size="sm">Always Underline</Link>
                      <Link color={color} underline="hover" size="sm">Hover Underline</Link>
                      <Link color={color} underline="none" size="sm">No Underline</Link>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CardBody>
      </Card>
    </div>
  );
};